import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { MessageCircle } from "lucide-react";

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

export default function Chats() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/messages/threads");
        setThreads(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F17] pb-28">
      <TopBar />
      <main className="max-w-2xl mx-auto pt-20 px-4">
        <div className="mb-6">
          <div className="overline text-rose-400 mb-1">CONVERSATIONS</div>
          <h1 className="font-display font-black text-3xl tracking-tight">Your chats.</h1>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-[#131A26] rounded-xl animate-pulse" />)}
          </div>
        ) : threads.length === 0 ? (
          <div className="glass rounded-3xl p-8 text-center" data-testid="chats-empty">
            <MessageCircle size={40} className="mx-auto text-rose-400 mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">No messages yet</h3>
            <p className="text-slate-400 text-sm">Match with someone or message an event host to start a chat.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {threads.map((t) => (
              <Link
                key={t.thread_key}
                to={`/chat/${t.other_user.id}`}
                data-testid={`thread-${t.other_user.id}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-[#131A26] border border-white/10 hover:border-white/25 transition-colors"
              >
                <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-violet-500 to-rose-500 flex items-center justify-center font-display font-bold shrink-0">
                  {t.other_user.photo_url ? (
                    <img src={t.other_user.photo_url} alt={t.other_user.name} className="w-full h-full object-cover" />
                  ) : (
                    t.other_user.name?.[0]?.toUpperCase() || "?"
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline">
                    <div className="font-semibold truncate">{t.other_user.name}</div>
                    <div className="text-xs text-slate-500 shrink-0 ml-2">{timeAgo(t.last_at)}</div>
                  </div>
                  <div className="text-sm text-slate-400 truncate">{t.last_message}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
